// The approval inbox (`SC-F810`): `GET /v1/instances/{id}/approvals` and
// `POST /v1/instances/{id}/approvals/{approvalId}/decision`.
//
// # What this surface is, and the one thing every reader has to know about it
//
// An `ApprovalPolicy` (`SC-F519`) holds a matching query until a human decides.
// This module reads that queue and submits the decision.
//
// **The decision is a PROXIED capability call.** Sluice's accept and reject
// endpoints are public and the capability token in the URL is the entire
// authorization upstream; the plaintext exists only long enough to build the
// notification URLs, which are delivered exclusively by webhook. There is no
// admin-authenticated decision endpoint on this build (`SLUICE-U11`). So Sluice
// Cloud is registered as the webhook target, holds the delivered URLs **sealed**,
// and POSTs the matching one server-side when a decision is submitted here.
//
// The consequence for this module is a rule with no exception: **there is no
// capability URL in any type below and there must never be one.** The API does not
// serve them, and a client that acquired one would be re-broadcasting a
// decision-granting credential — which `concept.md` §9 makes an explicit non-goal
// ("no approval decisions by email link from Sluice Cloud"). Nothing here builds a
// mailto:, a copy affordance or a share link.
//
// # The asymmetry
//
// [decideApproval] takes a `verification` only on the approve path. Approving lets
// a query policy refused run against production data, so it consumes a step-up
// grant for the `approve_query` action bound to the instance; rejecting reduces
// authority and is deliberately ungated, so the safe answer stays the quick one.
// A page that put a verification dialog in front of both would train an approver
// to click through it.

import { getJSON, newIdempotencyKey, query, sendJSON, type PageParams } from "./http.js";

/**
 * The stored lifecycle of an approval request. It is Sluice's own vocabulary minus
 * `consumed` — that transition happens inside the instance after the decision and
 * no webhook reports it, so this product never sees it.
 */
export type ApprovalState = "pending" | "approved" | "rejected" | "expired";

/** The four states in the order the queue reads: what needs doing, then what was
 * done, then what was missed. */
export const APPROVAL_STATES: ApprovalState[] = ["pending", "approved", "rejected", "expired"];

/** Human labels for the filter picker and the badges. */
export const APPROVAL_STATE_LABELS: Record<ApprovalState, string> = {
  pending: "Waiting",
  approved: "Approved",
  rejected: "Rejected",
  expired: "Expired",
};

/** One approval request. */
export interface ApprovalRequest {
  /** The control-plane record id — a stable key for a list row. */
  id: string;
  /** Sluice's own approval id. It is what every ACTION addresses, and what appears
   * in the instance's audit trail and logs, so a support conversation and a URL
   * name the same thing. */
  approval_id: string;
  instance: string;
  /** The instance-side subject whose query is waiting. It is the only subject
   * field stored: the issuer, the email and the group list stay in the instance's
   * own audit record rather than being copied into the control plane. */
  subject_id: string;
  /** The statement sample the instance sent, capped at 4000 bytes. Empty when the
   * instance is configured to send none (`approval.sqlSampleBytes: 0`). */
  sql_sample: string;
  /** True when `sql_sample` is shorter than the statement. It must be rendered:
   * a reviewer approving a statement has to know they are not looking at all of
   * it. */
  sql_truncated: boolean;
  /** The `ApprovalPolicy` names that demanded the approval. */
  policies: string[];
  /**
   * The EFFECTIVE state. A request whose deadline has passed reads `expired` even
   * before the server's expiry sweep has touched the row, so the queue never
   * offers a decidable request whose window closed a minute ago.
   */
  state: ApprovalState;
  /** Whether this request can be acted on. **Disable buttons from this field**
   * rather than re-deriving the rule from `state` and `expires_at`. */
  decidable: boolean;
  /** A sentence for every state that cannot be acted on — expiry is a distinct,
   * explained outcome and not an error. Empty while pending. */
  explanation?: string;
  requested_at: string;
  expires_at: string;
  /** The deciding account's record id, absent when nobody decided. */
  decided_by?: string;
  /** That account's email. Absent for a request the instance decided through a
   * notification link, which is the one case where the approver's identity is
   * genuinely unknown to us. */
  decided_by_label?: string;
  /** Null while undecided — never a zero timestamp. */
  decided_at: string | null;
}

/**
 * What this surface can and cannot do, stated by the server in the payload rather
 * than in a footnote (`concept.md` §7 principle 8).
 *
 * The page renders these verbatim. They are not decoration: `receiver_configured`
 * false means no instance is told where to deliver, so an empty queue means
 * "nothing can arrive" rather than "nothing is waiting" — which is the difference
 * between a calm screen and a silently broken feature.
 */
export interface ApprovalLimits {
  /** `proxied_capability` on this build. It would be `admin` if `SLUICE-U11`
   * existed. */
  decision_channel: string;
  receiver_configured: boolean;
  grant_requires_step_up: boolean;
  deny_requires_step_up: boolean;
  min_role_decide: string;
  /** Honest statements, rendered verbatim. */
  notes: string[];
}

export interface ApprovalPage {
  items: ApprovalRequest[];
  next: string | null;
  limits: ApprovalLimits;
}

/** The filters `GET /v1/instances/{id}/approvals` accepts. `state` filters on the
 * EFFECTIVE state, so `pending` excludes an overdue request and `expired`
 * includes it. */
export interface ApprovalQuery extends PageParams {
  state?: ApprovalState | "";
}

/** listApprovals reads one page of the queue, newest first. Requires `viewer`. */
export function listApprovals(
  instanceId: string,
  params: ApprovalQuery = {},
): Promise<ApprovalPage> {
  return getJSON<ApprovalPage>(
    `/v1/instances/${encodeURIComponent(instanceId)}/approvals` +
      query({
        state: params.state,
        limit: params.limit,
        cursor: params.cursor,
      }),
  );
}

/** The two decisions. They are the product's words rather than the upstream path
 * segments: an operator approves a query, they do not "accept" one. */
export type ApprovalDecision = "approve" | "reject";

/**
 * decideApproval submits a decision and returns the decided request.
 *
 * `verification` is a single-use step-up grant for the `approve_query` action on
 * this instance and is **required to approve, ignored when rejecting**. Obtain one
 * with `verifyWithPasskey({ action: "approve_query", target: instanceId })` or the
 * OTP pair.
 *
 * The `Idempotency-Key` is minted here per call, which is right for this route and
 * worth knowing: the server honours it against the ROW rather than a key store, so
 * a retry that finds the request already in the state this account asked for
 * replays a 200 instead of a 409. Minting one per attempt therefore costs nothing,
 * while a shared key would buy nothing either.
 *
 * `reason` is recorded in the organization's activity feed and nowhere else —
 * Sluice's capability endpoint accepts no reason field, so the instance's own audit
 * record cannot carry it.
 */
export function decideApproval(
  instanceId: string,
  approvalId: string,
  decision: ApprovalDecision,
  opts: { reason?: string; verification?: string } = {},
): Promise<ApprovalRequest> {
  return sendJSON<ApprovalRequest>(
    `/v1/instances/${encodeURIComponent(instanceId)}/approvals/${encodeURIComponent(approvalId)}/decision`,
    "POST",
    { decision, reason: opts.reason ?? "" },
    { idempotencyKey: newIdempotencyKey(), verification: opts.verification },
  );
}

/**
 * remaining renders how long a pending request has left, or how long ago it
 * lapsed.
 *
 * It is here rather than in the page because the number is the most decision-
 * relevant thing on the screen and the rounding has to be consistent: an approver
 * reading "2 min" has to be able to trust that it is not 20 seconds.
 */
export function remaining(expiresAt: string, now: number = Date.now()): string {
  const deadline = new Date(expiresAt).getTime();
  if (Number.isNaN(deadline)) return "—";
  const ms = deadline - now;
  if (ms <= 0) return "expired";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s} s left`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min left`;
  return `${Math.floor(m / 60)} h ${m % 60} min left`;
}
